import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { catalog, ContentType, TmdbNotConfiguredError, isFutureDated } from "../api/tmdb";
import { episodeCards, fetchLibraryRows } from "../api/library";
import { backdropUrl, posterUrl } from "../api/images";
import HeroCarousel from "../components/HeroCarousel";
import PosterRow from "../components/PosterRow";
import { LoadingScreen, ErrorScreen } from "../components/StatusScreens";
import { useWatchlist } from "../storage/watchlist";
import { useProgress, continueWatchingItems, getProgress } from "../storage/progress";
import { formatPosition } from "../storage/progress";
import { useAccount } from "../api/auth.js";

function ContinueWatchingRow() {
  const navigate = useNavigate();
  const progress = useProgress();
  const account = useAccount();
  // Continue Watching is account-gated: it only shows for signed-in users so
  // their progress syncs across web + TV. Signed-out visitors see no row.
  const items = account?.token ? continueWatchingItems(progress) : [];
  if (items.length === 0) return null;

  const play = (p) => {
    const isEpisode = p.contentType === ContentType.EPISODE;
    const params = new URLSearchParams({
      type: isEpisode ? "episode" : "movie",
      id: String(p.tmdbId),
      title: p.title,
      poster: p.posterPath || "",
      backdrop: p.backdropPath || "",
      subtitle: p.subtitle || "",
      resume: String(p.positionSeconds || 0),
    });
    if (isEpisode) {
      params.set("season", String(p.season ?? 0));
      params.set("episode", String(p.episode ?? 0));
    }
    navigate(`/watch?${params.toString()}`);
  };

  return (
    <section className="row continue-row" aria-label="Continue Watching">
      <h2 className="row-title">Continue Watching</h2>
      <div className="row-scroller">
        {items.map((p) => {
          const url = p.posterPath ? posterUrl(p.posterPath) : backdropUrl(p.backdropPath, { hero: false });
          const fraction = Math.min(1, p.positionSeconds / (p.durationSeconds || 1));
          return (
            <button key={p.contentType + p.tmdbId + (p.season || 0) + (p.episode || 0)} className="poster-card" onClick={() => play(p)} aria-label={`Resume ${p.title}`}>
              <div className="poster-frame">
                {url ? <img className="poster-img" src={url} alt="" loading="lazy" /> : <div className="poster-fallback"><span>{p.title}</span></div>}
                <div className="poster-hover">
                  <span className="poster-play" aria-hidden="true">▶</span>
                </div>
                <div className="poster-progress">
                  <div className="poster-progress-fill" style={{ width: `${fraction * 100}%` }} />
                </div>
              </div>
              <span className="poster-card-info">
                <span className="poster-card-title">{p.title}</span>
                <span className="poster-card-meta">
                  <span className="poster-card-rating">{formatPosition(p.positionSeconds)} left</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function MyListRow() {
  const navigate = useNavigate();
  const list = useWatchlist().filter((item) => !isFutureDated(item.date));
  if (list.length === 0) return null;
  return (
    <section className="row" aria-label="My List">
      <h2 className="row-title">My List</h2>
      <div className="row-scroller">
        {list.map((item) => (
          <button
            key={`${item.contentType}-${item.tmdbId}`}
            className="poster-card"
            onClick={() => navigate(item.contentType === "TV_SHOW" ? `/show/${item.tmdbId}` : `/movie/${item.tmdbId}`)}
            aria-label={item.title}
          >
            <div className="poster-frame">
              {item.posterPath ? (
                <img className="poster-img" src={posterUrl(item.posterPath)} alt="" loading="lazy" />
              ) : (
                <div className="poster-fallback"><span>{item.title}</span></div>
              )}
              <div className="poster-hover">
                <span className="poster-play" aria-hidden="true">▶</span>
              </div>
            </div>
            <span className="poster-card-info">
              <span className="poster-card-title">{item.title}</span>
              <span className="poster-card-meta">
                <span className="poster-card-type">{item.contentType === "TV_SHOW" ? "TV Show" : "Movie"}</span>
              </span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function HomeScreen() {
  const [state, setState] = useState({ loading: true, feed: null, error: null });
  // Manual (non-TMDB) titles — a show like "Big Boss" lists here as its own row
  // of episode cards. Loaded separately from the TMDB feed: it is local and
  // instant, and a failure must not take the home page down with it.
  const [libraryRows, setLibraryRows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetchLibraryRows().then((rows) => {
      if (!cancelled) setLibraryRows(rows);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const load = useCallback(() => {
    setState({ loading: true, feed: null, error: null });
    catalog
      .homeFeed()
      .then((feed) => setState({ loading: false, feed }))
      .catch((error) => {
        const message =
          error instanceof TmdbNotConfiguredError
            ? error.message
            : error.message || "Something went wrong. Try again.";
        setState({ loading: false, feed: null, error: message });
      });
  }, []);

  useEffect(load, [load]);

  if (state.loading) return <LoadingScreen label="Loading…" />;
  if (state.error) return <ErrorScreen message={state.error} onRetry={load} />;
  if (!state.feed) return <ErrorScreen message="The feed is empty." onRetry={load} />;

  const { slides, rows } = state.feed;
  const getCardProgress = (card) => {
    if (card.contentType === ContentType.EPISODE) return null;
    return getProgress(card.contentType, card.tmdbId);
  };

  return (
    <main className="page home-page">
      {slides.length > 0 && <HeroCarousel slides={slides} />}
      <div className="page-content">
        <ContinueWatchingRow />
        <MyListRow />
        {libraryRows.map((row) => (
          // A title whose artwork is a landscape banner asks for the 16:9 cards;
          // the portrait default would crop it.
          <PosterRow key={row.id} title={row.title} items={episodeCards(row)} wide={row.landscape} />
        ))}
        {rows.map((row) => (
          <PosterRow key={row.id} title={row.title} items={row.items} getProgress={getCardProgress} />
        ))}
      </div>
    </main>
  );
}